import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, post, errMsg, inr, dt, label, PRODUCT } from "@/lib/api";
import { PageHeader, Card, StatusBadge, Btn, Field, Table, Loading, useFetch } from "@/components/kit";

export function CustomerHome() {
  const nav = useNavigate();
  const { data, loading } = useFetch(() => get("/applications", { size: 50 }), []);
  if (loading) return <Loading />;
  const items = data.items;
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="My applications" subtitle="Track your loan applications in real time.">
        <Btn data-testid="customer-apply-pl" onClick={() => nav("/apply/PL")}>Apply Personal Loan</Btn>
        <Btn variant="outline" data-testid="customer-apply-bl" onClick={() => nav("/apply/BL")}>Apply Business Loan</Btn>
        <Btn variant="outline" data-testid="customer-apply-hl" onClick={() => nav("/apply/HL")}>Apply Home Loan</Btn>
        <Btn variant="outline" data-testid="customer-apply-cl" onClick={() => nav("/apply/CL")}>Apply Car Loan</Btn>
      </PageHeader>
      {items.length === 0 && <Card><p className="py-8 text-center text-sm text-slate-500" data-testid="no-applications">You have no applications yet. Start one above.</p></Card>}
      <div className="space-y-4">
        {items.map((a) => (
          <button key={a.id} data-testid={`customer-app-${a.app_code}`} onClick={() => nav(`/portal/applications/${a.id}`)} className="block w-full rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm hover:border-blue-300">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><p className="font-mono text-xs text-slate-400">{a.app_code}</p><p className="font-display text-lg font-semibold">{PRODUCT[a.product]} · {inr(a.loan_amount)}</p></div>
              <StatusBadge value={a.stage} />
            </div>
            <p className="mt-2 text-xs text-slate-500">KYC: {label(a.kyc_status)} · Lender: {a.selected_lender_name || "—"} · Updated {dt(a.updated_at)}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function Consents() {
  const { data, loading } = useFetch(() => get("/consents/me"), []);
  if (loading) return <Loading />;
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Consent history" subtitle="Every consent you gave, with version, time and device." />
      <Table testid="consent-history-table" rows={data} cols={[{ k: "type", h: "Type", r: (c) => label(c.type) }, { k: "text", h: "Text", r: (c) => <span className="block max-w-md whitespace-normal text-xs">{c.text}</span> },
        { k: "version", h: "Ver" }, { k: "status", h: "Status", r: (c) => <StatusBadge value={c.status} /> }, { k: "ip", h: "IP" }, { k: "timestamp", h: "At", r: (c) => dt(c.timestamp) }]} />
    </div>
  );
}

export function Support() {
  const { data, reload } = useFetch(() => get("/tickets"), []);
  const [f, setF] = useState({ category: "QUERY", priority: "MEDIUM" });
  const submit = async () => {
    try { const r = await post("/tickets", f); toast.success(`Ticket ${r.data.ticket_code} raised`); setF({ category: "QUERY", priority: "MEDIUM" }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const FAQ = [["Is FinOrbit a lender?", "No. FinOrbit Finance Pvt Ltd is a Loan Service Provider. Loans are sanctioned and disbursed by partner lenders."],
    ["How long does approval take?", "Timelines depend on the lender's underwriting. You can track status live in My Applications."],
    ["Is my data safe?", "Data is encrypted, access-controlled and shared only with lenders you consent to."]];
  return (
    <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <PageHeader title="Help & Support" subtitle="Raise a grievance or query. Grievance contact: info@finorbitfinance.com · +91-9824053365" />
        <Card title="Raise a ticket">
          <div className="space-y-3">
            <Field label="Category" name="category" form={f} setForm={setF} options={["COMPLAINT", "QUERY", "TECHNICAL", "LENDER_ISSUE", "PAYMENT_ISSUE", "DOCUMENT_ISSUE"]} />
            <Field label="Priority" name="priority" form={f} setForm={setF} options={["LOW", "MEDIUM", "HIGH"]} />
            <Field label="Subject" name="subject" form={f} setForm={setF} />
            <Field label="Description" name="description" type="textarea" form={f} setForm={setF} />
            <Btn data-testid="ticket-submit-btn" onClick={submit}>Submit</Btn>
          </div>
        </Card>
        <Card title="FAQ">{FAQ.map(([q, a]) => <div key={q} className="mb-3"><p className="text-sm font-semibold">{q}</p><p className="text-xs text-slate-500">{a}</p></div>)}</Card>
      </div>
      <Card title="My tickets">
        <div className="space-y-3" data-testid="my-tickets">
          {(data || []).map((t) => <div key={t.id} className="rounded-lg border p-3"><div className="flex justify-between"><p className="font-mono text-xs">{t.ticket_code}</p><StatusBadge value={t.status} /></div><p className="text-sm font-semibold">{t.subject}</p><p className="text-xs text-slate-500">{label(t.category)} · SLA {dt(t.sla_due)}</p>{t.resolution && <p className="mt-1 text-xs text-emerald-700">{t.resolution}</p>}</div>)}
          {data?.length === 0 && <p className="text-sm text-slate-400">No tickets.</p>}
        </div>
      </Card>
    </div>
  );
}
